import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import RemainingCampaignImpressionsBudgetFields from '../../../../UploadAdCreative/ImpressionsBudgetSection/RemainingCampaignImpressionsBudgetFields'
import EditForm from '../../../../../../features/components/Forms/EditForm'

import {
  campaignUpdateErrorSelector,
  campaignUpdateIsLoadingSelector,
  campaignWasUpdatedSelector
} from '../../../../../../modules/selectors/campaigns'
import { selectedTikTokAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import {
  calculateBudgetFromImpressions,
  calculateImpressionsFromBudget
} from '../../../../UploadAdCreative/ImpressionsBudgetSection/helpers'
import { filterObjectEmptyValues } from '../../../../../../helpers/common'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { clearUpdateCampaign, updateCampaign } from '../../../../../../modules/actions/campaigns'
import { rightSelector } from '../../../../../../modules/selectors/rights'

import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { TIKTOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'

import { IMPRESSIONS } from '../../../../UploadAdCreative/fields'
import { getImpressionsValidationsSchema } from '../../../../../ReusableFormSections/reusableBudgetSections/remainingImpressionsValidation'
import { BUDGET } from '../../../../../Tiktok/LineItemForms/LineItemTikTokEdit/fields'

const TikTokCampaignImpressionsBudgetSectionForm = ({
  campaign,
  providedAdAccountId,
  updateType,
  remainingImpressions,
  title,
  ...formProps
}) => {
  const { bid_cap: rightBidCap } = useSelector(rightSelector)
  const dispatch = useDispatch()

  const campaignWasUpdated = useSelector(campaignWasUpdatedSelector)
  const { id: selectedTikTokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const adAccountId = providedAdAccountId || selectedTikTokAdAccountId

  const handleSubmit = useCallback(
    values => {
      const budget = calculateBudgetFromImpressions({
        impressions: values[IMPRESSIONS],
        rightBidCap
      })

      const updateCampaignData = {
        ...filterObjectEmptyValues(campaign),
        [BUDGET]: Number(budget),
        account: adAccountId,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      // we use PUT for update TikTok campaign
      dispatch(
        updateCampaign(updateCampaignData, campaign.id, TIKTOK_PLATFORM, {
          updateType,
          requestMethod: 'PUT'
        })
      )
    },
    [campaign, adAccountId, title, updateType, dispatch, rightBidCap]
  )

  const initialCampaignImpressions = useMemo(() => {
    return calculateImpressionsFromBudget({
      budget: campaign?.[BUDGET],
      rightBidCap
    })
  }, [campaign, rightBidCap])

  const initialValues = useMemo(() => {
    return {
      [IMPRESSIONS]: initialCampaignImpressions
    }
  }, [initialCampaignImpressions])

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: getImpressionsValidationsSchema(remainingImpressions, initialCampaignImpressions)
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateCampaign = useCallback(() => {
    dispatch(clearUpdateCampaign())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={campaignWasUpdated}
      clearEditItem={handleClearUpdateCampaign}
      errorSelector={campaignUpdateErrorSelector}
      isLoadingSelector={campaignUpdateIsLoadingSelector}
      {...formProps}
    >
      <RemainingCampaignImpressionsBudgetFields formik={formik} remainingImpressions={remainingImpressions} />
    </EditForm>
  )
}

export default TikTokCampaignImpressionsBudgetSectionForm
